<script>
import { ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getAllowedFileTypesByChannel } from '@chatwoot/utils';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import VideoCallButton from '../VideoCallButton.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { toast } from 'dashboard/composables/useToast';

export default {
  name: 'ReplyBottomPanel',
  components: { NextButton, FileUpload, VideoCallButton },
  mixins: [inboxMixin],
  props: {
    isNote: {
      type: Boolean,
      default: false,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    // inbox prop is used in /mixins/inboxMixin,
    // remove this props when refactoring to composable if not needed
    // eslint-disable-next-line vue/no-unused-properties
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    enableWhatsAppTemplates: {
      type: Boolean,
      default: false,
    },
    enableContentTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    // eslint-disable-next-line vue/no-unused-properties
    message: {
      type: String,
      default: '',
    },
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    portalSlug: {
      type: String,
      required: true,
    },
    conversationType: {
      type: String,
      default: '',
    },
    showQuotedReplyToggle: {
      type: Boolean,
      default: false,
    },
    quotedReplyEnabled: {
      type: Boolean,
      default: false,
    },
    templateOnlyMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'replaceText',
    'toggleInsertArticle',
    'selectWhatsappTemplate',
    'selectContentTemplate',
    'toggleQuotedReply',
    'openScheduleDialog',
  ],
  setup() {
    const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings } =
      useUISettings();

    const uploadRef = ref(false);

    const keyboardEvents = {
      '$mod+Alt+KeyA': {
        action: () => {
          // TODO: This is really hacky, we need to replace the file picker component with
          // a custom one, where the logic and the component markup is isolated.
          // Once we have the custom component, we can remove the hacky logic below.

          const uploadTriggerButton = document.querySelector(
            '#conversationAttachment'
          );
          uploadTriggerButton.click();
        },
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      uploadRef,
    };
  },
  data() {
    return {
      macroBusca: '',
      ALLOWED_FILE_TYPES,
      showMacroMenu: false,
      showPlusMenu: false,
      executingMacroId: null,
      // gesto de gravacao no touch (segurar/travar/cancelar)
      holdRec: {
        holding: false,
        locked: false,
        started: false,
        startedAt: 0,
        startX: 0,
        startY: 0,
        timer: null,
        suppressClickUntil: 0,
      },
    };
  },
  computed: {
    macrosFiltradas() {
      const q = this.macroBusca.trim().toLowerCase();
      if (!q) return this.macros;
      return this.macros.filter(m => m.name?.toLowerCase().includes(q));
    },
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'integrations/getUIFlags',
      macros: 'macros/getMacros',
    }),
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    showAttachButton() {
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      if (this.isALineChannel) {
        return false;
      }
      // Disable audio recorder for safari browser as recording is not supported
      // const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
      //   navigator.userAgent
      // );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) && this.showAudioRecorder
        // !isSafari
      );
    },
    showAudioPlayStopButton() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    allowedFileTypes() {
      // Use default file types for private notes
      if (this.isOnPrivateNote) {
        return this.ALLOWED_FILE_TYPES;
      }

      let channelType = this.channelType || this.inbox?.channel_type;

      if (this.isAnInstagramChannel || this.isInstagramDM) {
        channelType = INBOX_TYPES.INSTAGRAM;
      }

      return getAllowedFileTypesByChannel({
        channelType,
        medium: this.inbox?.medium,
      });
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
          return 'i-ph-play';
        case 'stopped':
          return 'i-ph-play';
        default:
          return 'i-ph-stop';
      }
    },
    showMessageSignatureButton() {
      return !this.isOnPrivateNote;
    },
    sendWithSignature() {
      // channelType is sourced from inboxMixin
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    enableInsertArticleInReply() {
      return this.portalSlug;
    },
    isFetchingAppIntegrations() {
      return this.uiFlags.isFetching;
    },
    quotedReplyToggleTooltip() {
      return this.quotedReplyEnabled
        ? this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.DISABLE_TOOLTIP')
        : this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.ENABLE_TOOLTIP');
    },
    showPlusMenuButton() {
      return this.enableWhatsAppTemplates || this.enableContentTemplates;
    },
    // recordingAudioState fica '' enquanto grava; 'stopped'/'playing'/'paused'
    // depois que o audio ja virou anexo — ai o botao volta a ser "enviar"
    sendMorphState() {
      if (this.templateOnlyMode) return 'template';
      if (this.isRecordingAudio && !this.recordingAudioState) return 'stop';
      if (!this.isSendDisabled) return 'send';
      if (this.showAudioRecorderButton) return 'record';
      return 'send';
    },
    sendMorphIcon() {
      if (this.sendMorphState === 'template') return 'i-ph-whatsapp-logo';
      if (this.sendMorphState === 'stop') return 'i-ph-stop-fill';
      if (this.sendMorphState === 'record') return 'i-ph-microphone';
      return 'i-ph-paper-plane-right-fill';
    },
    sendMorphDisabled() {
      return this.sendMorphState === 'send' && this.isSendDisabled;
    },
    sendMorphTooltip() {
      if (this.sendMorphState === 'template')
        return this.$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES');
      if (this.sendMorphState === 'stop')
        return this.$t('CONVERSATION.REPLYBOX.STOP_AUDIO_RECORDING');
      if (this.sendMorphState === 'record')
        return this.$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON');
      return this.sendButtonText;
    },
  },
  mounted() {
    ActiveStorage.start();
    this.$store.dispatch('macros/get');
  },
  beforeUnmount() {
    window.removeEventListener('click', this.closeMacroMenu);
    window.removeEventListener('click', this.closePlusMenu);
    clearTimeout(this.holdRec.timer);
  },
  methods: {
    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    replaceText(text) {
      this.$emit('replaceText', text);
    },
    toggleInsertArticle() {
      this.$emit('toggleInsertArticle');
    },
    toggleMacroMenu() {
      this.showMacroMenu = !this.showMacroMenu;
      if (this.showMacroMenu) {
        setTimeout(() => {
          window.addEventListener('click', this.closeMacroMenu);
        }, 0);
      }
    },
    closeMacroMenu() {
      this.showMacroMenu = false;
      window.removeEventListener('click', this.closeMacroMenu);
    },
    togglePlusMenu() {
      this.showPlusMenu = !this.showPlusMenu;
      if (this.showPlusMenu) {
        setTimeout(() => {
          window.addEventListener('click', this.closePlusMenu);
        }, 0);
      }
    },
    closePlusMenu() {
      this.showPlusMenu = false;
      window.removeEventListener('click', this.closePlusMenu);
    },
    onPlusMenuAction(event) {
      this.closePlusMenu();
      if (event === 'selectWhatsappTemplate') {
        this.$emit('selectWhatsappTemplate');
      } else if (event === 'selectContentTemplate') {
        this.$emit('selectContentTemplate');
      } else if (event === 'openScheduleDialog') {
        this.$emit('openScheduleDialog');
      } else if (event === 'toggleQuotedReply') {
        this.$emit('toggleQuotedReply');
      } else if (event === 'toggleInsertArticleEvent') {
        this.toggleInsertArticle();
      }
    },
    // ===== gesto WhatsApp de audio (so touch) =====
    onMorphTouchStart(event) {
      if (this.sendMorphState !== 'record') return;

      const touch = event.touches?.[0];
      if (!touch) return;

      this.holdRec.holding = true;
      this.holdRec.locked = false;
      this.holdRec.started = false;
      this.holdRec.startX = touch.clientX;
      this.holdRec.startY = touch.clientY;
      // segura 150ms antes de gravar pra diferenciar de tap acidental
      this.holdRec.timer = setTimeout(() => {
        if (!this.holdRec.holding) return;
        this.holdRec.started = true;
        this.holdRec.startedAt = Date.now();
        this.toggleAudioRecorder();
      }, 150);
    },
    onMorphTouchMove(event) {
      if (!this.holdRec.holding || this.holdRec.locked) return;

      const touch = event.touches?.[0];
      if (!touch) return;

      event.preventDefault();
      const dy = this.holdRec.startY - touch.clientY;
      const dx = this.holdRec.startX - touch.clientX;
      if (this.holdRec.started && dy > 60) {
        // deslizou pra cima: trava maos-livres (solta o dedo, segue gravando)
        this.holdRec.locked = true;
        this.holdRec.holding = false;
        this.holdRec.suppressClickUntil = Date.now() + 400;
      } else if (this.holdRec.started && dx > 80) {
        // deslizou pra esquerda: cancela e descarta
        this.cancelHoldRecording();
      }
    },
    onMorphTouchEnd() {
      clearTimeout(this.holdRec.timer);
      if (this.holdRec.locked) return;
      if (!this.holdRec.holding) return;

      this.holdRec.holding = false;
      this.holdRec.suppressClickUntil = Date.now() + 400;
      if (!this.holdRec.started) {
        toast.warning(this.$t('CONVERSATION.REPLYBOX.HOLD_TO_RECORD'));
        return;
      }
      // gravacao curta demais nao vira audio decente: descarta (WhatsApp faz igual)
      if (Date.now() - this.holdRec.startedAt < 1000) {
        this.cancelHoldRecording();
        toast.error(this.$t('CONVERSATION.REPLYBOX.AUDIO_TOO_SHORT'));
        return;
      }
      // soltou: para a gravacao MANTENDO o audio (vira anexo)
      this.toggleAudioRecorderPlayPause();
      this.holdRec.started = false;
    },
    // touchcancel = algo interrompeu o gesto (teclado fechando, scroll do
    // navegador): descarta em vez de "parar e manter" um audio quebrado
    onMorphTouchCancelled() {
      if (!this.holdRec.holding && !this.holdRec.started) return;

      this.cancelHoldRecording();
    },
    cancelHoldRecording() {
      clearTimeout(this.holdRec.timer);
      this.holdRec.holding = false;
      this.holdRec.locked = false;
      this.holdRec.started = false;
      this.holdRec.suppressClickUntil = Date.now() + 400;
      // descarta a gravacao em andamento
      if (this.isRecordingAudio) this.toggleAudioRecorder();
    },
    onSendMorphClick() {
      // click sintetico logo apos o gesto de touch: ignora
      if (Date.now() < this.holdRec.suppressClickUntil) return;
      // parou pela trava maos-livres: some o aviso pulsante
      if (this.holdRec.locked) this.holdRec.locked = false;
      if (this.sendMorphState === 'template') {
        if (this.enableWhatsAppTemplates) this.$emit('selectWhatsappTemplate');
        else if (this.enableContentTemplates)
          this.$emit('selectContentTemplate');
        return;
      }
      if (this.sendMorphState === 'stop') {
        // para a gravacao mantendo o audio (vira anexo via finish-record)
        this.toggleAudioRecorderPlayPause();
        return;
      }
      if (this.sendMorphState === 'record') {
        this.toggleAudioRecorder();
        return;
      }
      this.onSend();
    },
    async executeMacro(macro) {
      this.executingMacroId = macro.id;
      try {
        await this.$store.dispatch('macros/execute', {
          macroId: macro.id,
          conversationIds: [this.conversationId],
        });
        toast.success(this.$t('MACROS.EXECUTE.EXECUTED_SUCCESSFULLY'));
      } catch {
        toast.error(this.$t('MACROS.ERROR'));
      } finally {
        this.executingMacroId = null;
        this.closeMacroMenu();
        this.closePlusMenu();
      }
    },
  },
};
</script>

<template>
  <div class="py-1" :class="wrapClass">
    <div class="flex items-end gap-0.5">
      <!-- esquerda: emoji + anexo (gravando: descartar + play/pause) -->
      <div
        v-if="!isRecordingAudio"
        class="flex items-end gap-0.5 mb-1"
        :class="{ 'opacity-40 pointer-events-none': templateOnlyMode }"
      >
        <NextButton
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
          icon="i-ph-smiley-sticker"
          slate
          ghost
          sm
          @click="toggleEmojiPicker"
        />
        <FileUpload
          ref="uploadRef"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          input-id="conversationAttachment"
          :size="4096 * 4096"
          :accept="allowedFileTypes"
          :multiple="enableMultipleFileUpload"
          :drop="enableDragAndDrop"
          :drop-directory="false"
          :data="{
            direct_upload_url: '/rails/active_storage/direct_uploads',
            direct_upload: true,
          }"
          @input-file="onFileUpload"
        >
          <NextButton
            v-if="showAttachButton"
            v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
            icon="i-ph-paperclip"
            slate
            ghost
            sm
          />
        </FileUpload>
      </div>
      <div v-else class="flex items-end gap-0.5 mb-1">
        <NextButton
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.DISCARD_AUDIO')"
          icon="i-lucide-trash-2"
          slate
          ghost
          sm
          @click="toggleAudioRecorder"
        />
        <NextButton
          v-if="showAudioPlayStopButton"
          :icon="audioRecorderPlayStopIcon"
          slate
          ghost
          sm
          :label="recordingAudioDurationText"
          @click="toggleAudioRecorderPlayPause"
        />
      </div>

      <!-- o editor (ou o gravador) entra aqui de dentro do ReplyBox -->
      <div
        class="flex-1 min-w-0 self-center px-1 py-1 composer-editor"
        :class="{ 'opacity-50 pointer-events-none': templateOnlyMode }"
      >
        <slot />
      </div>

      <!-- direita (desktop): ferramentas todas FORA, na mesma linha -->
      <div
        v-if="!isRecordingAudio"
        class="hidden md:flex items-end gap-0.5 mb-1"
        :class="{ 'opacity-40 pointer-events-none': templateOnlyMode }"
      >
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('MACROS.RUN_MACRO')"
            icon="i-lucide-zap"
            slate
            ghost
            sm
            @click.stop="toggleMacroMenu"
          />
          <div
            v-if="showMacroMenu"
            class="absolute bottom-full right-0 mb-1.5 z-50 bg-n-solid-1 border border-n-weak rounded-xl shadow-xl py-1.5 min-w-[220px] max-h-[240px] overflow-y-auto slim-scrollbar"
            @click.stop
          >
            <!-- eslint-disable vue/no-bare-strings-in-template -->
            <div
              class="px-3 py-1 text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              Macros
            </div>
            <!-- eslint-enable vue/no-bare-strings-in-template -->
            <div class="px-2 pb-1.5">
              <input
                v-model="macroBusca"
                type="text"
                class="!mb-0 !h-7 w-full rounded-lg !text-xs"
                :placeholder="$t('MACROS.SEARCH_PLACEHOLDER')"
              />
            </div>
            <template v-if="macrosFiltradas.length">
              <button
                v-for="macro in macrosFiltradas"
                :key="macro.id"
                class="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                :disabled="executingMacroId === macro.id"
                @click.stop="executeMacro(macro)"
              >
                <span
                  v-if="executingMacroId === macro.id"
                  class="i-lucide-loader-2 size-3.5 animate-spin flex-shrink-0 text-n-slate-9"
                />
                <span
                  v-else
                  class="i-lucide-play size-3.5 flex-shrink-0 text-n-slate-9"
                />
                <span class="truncate">{{ macro.name }}</span>
              </button>
            </template>
            <div v-else class="px-3 py-2 text-xs text-n-slate-9">
              {{ $t('MACROS.LIST.404') }}
            </div>
          </div>
        </div>
        <!-- Assinatura desabilitada a pedido: a config por caixa continua
             valendo, so o toggle por mensagem saiu da regua -->
        <NextButton
          v-if="showQuotedReplyToggle"
          v-tooltip.top-end="quotedReplyToggleTooltip"
          icon="i-ph-quotes"
          :variant="quotedReplyEnabled ? 'solid' : 'ghost'"
          color="slate"
          sm
          :aria-pressed="quotedReplyEnabled"
          @click="$emit('toggleQuotedReply')"
        />
        <NextButton
          v-if="enableInsertArticleInReply"
          v-tooltip.top-end="
            $t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')
          "
          icon="i-ph-article-ny-times"
          slate
          ghost
          sm
          @click="toggleInsertArticle"
        />
        <VideoCallButton
          v-if="(isAWebWidgetInbox || isAPIInbox) && !isOnPrivateNote"
          :conversation-id="conversationId"
        />
        <NextButton
          v-if="!isOnPrivateNote"
          v-tooltip.top-end="$t('SCHEDULED_MESSAGES.SCHEDULE_BUTTON_TOOLTIP')"
          icon="i-lucide-calendar-clock"
          slate
          ghost
          sm
          @click="$emit('openScheduleDialog')"
        />
        <!-- desktop: templates ficam FORA (logo verde direto na regua) -->
        <NextButton
          v-if="enableWhatsAppTemplates && !templateOnlyMode"
          v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
          icon="i-ph-whatsapp-logo"
          slate
          ghost
          sm
          class="!text-[#1FA855]"
          @click="$emit('selectWhatsappTemplate')"
        />
        <NextButton
          v-if="enableContentTemplates && !templateOnlyMode"
          v-tooltip.top-end="'Content Templates'"
          icon="i-ph-whatsapp-logo"
          slate
          ghost
          sm
          class="!text-[#1FA855]"
          @click="$emit('selectContentTemplate')"
        />
      </div>

      <!-- mobile: TUDO dentro do "+" -->
      <div
        v-if="!isRecordingAudio"
        class="relative md:hidden mb-1"
        :class="{ 'opacity-40 pointer-events-none': templateOnlyMode }"
      >
        <NextButton
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.PLUS_MENU')"
          icon="i-lucide-plus"
          slate
          ghost
          sm
          @click.stop="togglePlusMenu"
        />
        <div
          v-if="showPlusMenu"
          class="absolute bottom-full right-0 mb-1.5 z-50 bg-n-solid-1 border border-n-weak rounded-xl shadow-xl py-1.5 min-w-[240px] max-h-[300px] overflow-y-auto slim-scrollbar"
          @click.stop
        >
          <button
            v-if="enableWhatsAppTemplates"
            class="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            @click.stop="onPlusMenuAction('selectWhatsappTemplate')"
          >
            <span
              class="i-ph-whatsapp-logo size-4 flex-shrink-0 text-[#1FA855]"
            />
            <span class="truncate">
              {{ $t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES') }}
            </span>
          </button>
          <button
            v-if="enableContentTemplates"
            class="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            @click.stop="onPlusMenuAction('selectContentTemplate')"
          >
            <span
              class="i-ph-whatsapp-logo size-4 flex-shrink-0 text-[#1FA855]"
            />
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            <span class="truncate">Content Templates</span>
          </button>
          <button
            v-if="!isOnPrivateNote"
            class="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            @click.stop="onPlusMenuAction('openScheduleDialog')"
          >
            <span
              class="i-lucide-calendar-clock size-4 flex-shrink-0 text-n-slate-9"
            />
            <span class="truncate">
              {{ $t('SCHEDULED_MESSAGES.SCHEDULE_BUTTON_TOOLTIP') }}
            </span>
          </button>
          <button
            v-if="showQuotedReplyToggle"
            class="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            @click.stop="onPlusMenuAction('toggleQuotedReply')"
          >
            <span class="i-ph-quotes size-4 flex-shrink-0 text-n-slate-9" />
            <span class="truncate">{{ quotedReplyToggleTooltip }}</span>
          </button>
          <button
            v-if="enableInsertArticleInReply"
            class="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            @click.stop="onPlusMenuAction('toggleInsertArticleEvent')"
          >
            <span
              class="i-ph-article-ny-times size-4 flex-shrink-0 text-n-slate-9"
            />
            <span class="truncate">
              {{ $t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH') }}
            </span>
          </button>
          <!-- eslint-disable vue/no-bare-strings-in-template -->
          <div
            class="px-3 pt-2 pb-1 text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
          >
            Macros
          </div>
          <!-- eslint-enable vue/no-bare-strings-in-template -->
          <div class="px-2 pb-1.5">
            <input
              v-model="macroBusca"
              type="text"
              class="!mb-0 !h-7 w-full rounded-lg !text-xs"
              :placeholder="$t('MACROS.SEARCH_PLACEHOLDER')"
            />
          </div>
          <template v-if="macrosFiltradas.length">
            <button
              v-for="macro in macrosFiltradas"
              :key="macro.id"
              class="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              :disabled="executingMacroId === macro.id"
              @click.stop="executeMacro(macro)"
            >
              <span
                v-if="executingMacroId === macro.id"
                class="i-lucide-loader-2 size-3.5 animate-spin flex-shrink-0 text-n-slate-9"
              />
              <span
                v-else
                class="i-lucide-play size-3.5 flex-shrink-0 text-n-slate-9"
              />
              <span class="truncate">{{ macro.name }}</span>
            </button>
          </template>
          <div v-else class="px-3 py-2 text-xs text-n-slate-9">
            {{ $t('MACROS.LIST.404') }}
          </div>
        </div>
      </div>

      <!-- botao unico redondo: mic -> enviar -> stop; janela fechada -> template.
           No touch, gravar e segurar (WhatsApp): soltar mantem o audio,
           deslizar pra cima trava maos-livres, deslizar pra esquerda cancela -->
      <div class="relative flex-shrink-0 ml-1">
        <div
          v-if="holdRec.holding && holdRec.started && !holdRec.locked"
          class="absolute bottom-full right-0 mb-2 whitespace-nowrap rounded-full bg-n-slate-12 text-n-slate-1 shadow-xl px-3 py-1.5 text-xs font-medium z-[70]"
        >
          {{ $t('CONVERSATION.REPLYBOX.RECORD_SLIDE_HINT') }}
        </div>
        <!-- Trava maos-livres ativa: aviso pulsante ate tocar no stop -->
        <div
          v-else-if="holdRec.locked && isRecordingAudio"
          class="absolute bottom-full right-0 mb-2 flex items-center gap-1.5 whitespace-nowrap rounded-full bg-n-ruby-9 px-3 py-1.5 text-xs font-medium text-white shadow-xl z-[70]"
        >
          <span class="size-2 rounded-full bg-white animate-pulse" />
          {{ $t('CONVERSATION.REPLYBOX.RECORDING_LOCKED') }}
        </div>
        <NextButton
          v-tooltip.top-end="sendMorphTooltip"
          :icon="sendMorphIcon"
          type="submit"
          :color="isNote ? 'amber' : 'blue'"
          :disabled="sendMorphDisabled"
          class="!rounded-full !w-10 !h-10 transition-transform"
          :class="{
            '!bg-[#1FA855] hover:!bg-[#178F47] !text-white':
              sendMorphState === 'template',
            'scale-125': holdRec.holding && !holdRec.locked,
          }"
          @click="onSendMorphClick"
          @touchstart="onMorphTouchStart"
          @touchmove="onMorphTouchMove"
          @touchend="onMorphTouchEnd"
          @touchcancel="onMorphTouchCancelled"
        />
      </div>
    </div>

    <transition name="modal-fade">
      <div
        v-show="uploadRef && uploadRef.dropActive"
        class="flex fixed top-0 right-0 bottom-0 left-0 z-20 flex-col gap-2 justify-center items-center w-full h-full text-n-slate-12 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
      >
        <fluent-icon icon="cloud-backup" size="40" />
        <h4 class="text-2xl break-words text-n-slate-12">
          {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
        </h4>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
/* dentro da barra o editor comeca com 1 linha e cresce (o global
   ProseMirror-woot-style impoe min-h de 5rem) */
.composer-editor ::v-deep(.ProseMirror-woot-style) {
  @apply min-h-6 max-h-40;
}

/* placeholder menor e discreto: o texto longo quebrava linha e abria
   rolagem no editor de 1 linha */
.composer-editor ::v-deep(.empty-node::before) {
  @apply text-[12.5px] text-n-slate-9;
}

::v-deep .file-uploads {
  label {
    @apply cursor-pointer;
  }

  &:hover button {
    @apply enabled:bg-n-slate-9/20;
  }
}
</style>
